// Credits: https://github.com/agilgur5/trim-canvas/blob/e2e607362cf5657d240aab6ddf1f6d35717f2822/src/index.js

/**
 * Trims transparent pixels from the edges of a canvas, resizing it to fit only visible content.
 *
 * This function scans from all four edges to find the boundaries of non-transparent pixels,
 * then crops the canvas to those boundaries. If the canvas is entirely transparent, it will
 * be left unchanged.
 *
 * @param canvas - The HTMLCanvasElement to trim
 * @returns The same canvas element (for method chaining), now trimmed to visible content
 *
 * @example
 * ```ts
 * const canvas = document.getElementById('myCanvas') as HTMLCanvasElement;
 * trimCanvas(canvas);
 * ```
 */
export default function trimCanvas(canvas: HTMLCanvasElement): HTMLCanvasElement {
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Unable to get 2D context from canvas');
  }

  const imgWidth = canvas.width;
  const imgHeight = canvas.height;

  const imageData = context.getImageData(0, 0, imgWidth, imgHeight);
  const imgData = imageData.data;

  // Find the boundaries of visible content
  const cropTop = scanY(true, imgWidth, imgHeight, imgData);
  const cropBottom = scanY(false, imgWidth, imgHeight, imgData);
  const cropLeft = scanX(true, imgWidth, imgHeight, imgData);
  const cropRight = scanX(false, imgWidth, imgHeight, imgData);

  // If any boundary is null, the entire canvas is transparent
  if (cropTop === null || cropBottom === null || cropLeft === null || cropRight === null) {
    return canvas;
  }

  // Calculate the dimensions of the trimmed region
  // +1 because we need inclusive bounds (e.g., pixels 10-20 = 11 pixels)
  const cropWidth = cropRight - cropLeft + 1;
  const cropHeight = cropBottom - cropTop + 1;

  // Extract the visible content region
  const trimmedData = context.getImageData(cropLeft, cropTop, cropWidth, cropHeight);

  // Resize the canvas to the trimmed dimensions
  canvas.width = cropWidth;
  canvas.height = cropHeight;

  // Clear and redraw with trimmed content
  context.clearRect(0, 0, cropWidth, cropHeight);
  context.putImageData(trimmedData, 0, 0);

  return canvas;
}

interface RGBA {
  red: number;
  green: number;
  blue: number;
  alpha: number;
}

/**
 * Gets the RGBA values of a pixel at the specified coordinates.
 *
 * @param x - The x-coordinate of the pixel
 * @param y - The y-coordinate of the pixel
 * @param imgWidth - The width of the image
 * @param imgData - The flat array of image data (RGBA values)
 * @returns An object containing the red, green, blue, and alpha values
 */
function getRGBA(x: number, y: number, imgWidth: number, imgData: Uint8ClampedArray): RGBA {
  const index = (imgWidth * y + x) * 4;
  return {
    red: imgData[index],
    green: imgData[index + 1],
    blue: imgData[index + 2],
    alpha: imgData[index + 3],
  };
}

/**
 * Gets the alpha (transparency) value of a pixel at the specified coordinates.
 *
 * @param x - The x-coordinate of the pixel
 * @param y - The y-coordinate of the pixel
 * @param imgWidth - The width of the image
 * @param imgData - The flat array of image data (RGBA values)
 * @returns The alpha value (0-255)
 */
function getAlpha(x: number, y: number, imgWidth: number, imgData: Uint8ClampedArray): number {
  return getRGBA(x, y, imgWidth, imgData).alpha;
}

/**
 * Scans vertically to find the first row containing non-transparent pixels.
 *
 * @param fromTop - If true, scans from top to bottom; if false, scans from bottom to top
 * @param imgWidth - The width of the image
 * @param imgHeight - The height of the image
 * @param imgData - The flat array of image data (RGBA values)
 * @returns The y-coordinate of the first row with visible content, or null if the image is fully transparent
 */
function scanY(fromTop: boolean, imgWidth: number, imgHeight: number, imgData: Uint8ClampedArray): number | null {
  const offset = fromTop ? 1 : -1;
  const firstRow = fromTop ? 0 : imgHeight - 1;

  for (let y = firstRow; fromTop ? y < imgHeight : y > -1; y += offset) {
    for (let x = 0; x < imgWidth; x += 1) {
      if (getAlpha(x, y, imgWidth, imgData)) {
        return y;
      }
    }
  }

  return null;
}

/**
 * Scans horizontally to find the first column containing non-transparent pixels.
 *
 * @param fromLeft - If true, scans from left to right; if false, scans from right to left
 * @param imgWidth - The width of the image
 * @param imgHeight - The height of the image
 * @param imgData - The flat array of image data (RGBA values)
 * @returns The x-coordinate of the first column with visible content, or null if the image is fully transparent
 */
function scanX(fromLeft: boolean, imgWidth: number, imgHeight: number, imgData: Uint8ClampedArray): number | null {
  const offset = fromLeft ? 1 : -1;
  const firstColumn = fromLeft ? 0 : imgWidth - 1;

  for (let x = firstColumn; fromLeft ? x < imgWidth : x > -1; x += offset) {
    for (let y = 0; y < imgHeight; y += 1) {
      if (getAlpha(x, y, imgWidth, imgData)) {
        return x;
      }
    }
  }

  return null;
}
